import {
  Component,
  Renderer2,
  OnInit,
  Inject,
  OnDestroy,
  AfterViewInit,
} from "@angular/core";
import { WritableSignal, signal } from "@angular/core";

import {
  MsalService,
  MsalBroadcastService,
  MSAL_GUARD_CONFIG,
  MsalGuardConfiguration,
} from "@azure/msal-angular";
import {
  AccountInfo,
  InteractionStatus,
  RedirectRequest,
  EventMessage,
  EventType,
} from "@azure/msal-browser";
import { filter, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { Theme } from "./theme/theme";
import { ThemeService } from "./theme/theme.service";
import { GtmAnalyticsService } from "./services/gtm-analytics.service";
import { CommonService } from "./services/common.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrl: "./app.component.scss",
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  clickEvent: string = "";
  currentTheme: Theme = Theme.LIGHT; // Set the default theme to light

  buttonLabel: string = "Display Message";
  statusCode: string = "";
  loginDisplay = false;
  isIframe = false;

  private readonly _destroying$ = new Subject<void>();

  account: WritableSignal<AccountInfo | null> = signal(null);

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private themeService: ThemeService,
    private renderer: Renderer2,
    private gtmAnalytics: GtmAnalyticsService,
    private commonService: CommonService
  ) {
    this.themeService.setTheme(this.currentTheme, this.renderer);
  }

  ngOnInit() {
    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.setLoginDisplay();
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED
        )
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = "/";
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
    this.getInitialDataLoad();

    const baseUrl = window.location.origin;
    if (
      baseUrl.includes("digitalam-cdt") ||
      baseUrl.includes("digitalam-stage") ||
      baseUrl.includes("digitalam-uat")
    ) {
      //set appname for GTM Analytics
      localStorage.setItem("analytics_appName", "badactor");
      // Initiate GTM analytics
      this.gtmAnalytics.loadAnalytics();
    }
  }

  getInitialDataLoad() {
    this.getUserFeaturePermissions();
  }
  getUserFeaturePermissions() {
    this.commonService.toggleRoleLoading(true);
    this.commonService.fetchUserFeaturePersmissions().subscribe({
      next: (resp: any) => {
        console.log(resp);
        if (resp && resp.data.features) {
          this.commonService.setuserFeaturePermissions(resp.data.features);
          this.commonService.setUserRole(resp.data.RoleName);
          this.commonService.toggleRoleLoading(false);
        }
      },
      error: (error) => {
         this.commonService.toggleRoleLoading(false);
        console.error("Error fetching user feature permissions", error);
      },
    });
  }
  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: "/",
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  ngAfterViewInit() {
    let loader = this.renderer.selectRootElement("#loader");
    this.renderer.setStyle(loader, "display", "none");
  }
}
